import { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Download, Upload, AlertTriangle, CheckCircle, Database, Trash2, Shield, Lock as LockIcon, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { getSecureStorage } from '../utils/secureStorage';
import { useSubscriptionPlan } from '../lib/use-subscription-plan';
import { safeParseJson } from '../lib/storage-utils';

/** Merge secure + plain localStorage arrays without double-counting duplicates (split-brain after migration). */
function combinedArrayLength(secure: unknown, plainRaw: string | null): number {
  const plain = safeParseJson<unknown[]>(plainRaw, []);
  const a = Array.isArray(secure) ? secure : [];
  const b = Array.isArray(plain) ? plain : [];
  if (a.length === 0 && b.length === 0) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  const seen = new Set<string>();
  for (const item of [...a, ...b]) {
    try {
      seen.add(JSON.stringify(item));
    } catch {
      seen.add(`x-${seen.size}`);
    }
  }
  return seen.size;
}

export function DataBackup() {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [stats, setStats] = useState({ checkins: 0, journal: 0, anxiety: 0, relationships: 0 });
  const { features, loading: subLoading, plan } = useSubscriptionPlan();

  const getDataStats = async () => {
    try {
      const storage = getSecureStorage();
      const [checkinsS, journalS, anxietyS, relS] = await Promise.all([
        storage.getItem('tension_checkins'),
        storage.getItem('tension_journal_entries'),
        storage.getItem('tension_anxiety_tracker'),
        storage.getItem('tension_relationships'),
      ]);
      return {
        checkins: combinedArrayLength(checkinsS, localStorage.getItem('tension_checkins')),
        journal: combinedArrayLength(journalS, localStorage.getItem('tension_journal_entries')),
        anxiety: combinedArrayLength(anxietyS, localStorage.getItem('tension_anxiety_tracker')),
        relationships: combinedArrayLength(relS, localStorage.getItem('tension_relationships')),
      };
    } catch {
      return { checkins: 0, journal: 0, anxiety: 0, relationships: 0 };
    }
  };

  useEffect(() => {
    const load = () => void getDataStats().then(setStats);
    load();
    const onVis = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  if (!subLoading && !features.exportData) {
    return (
      <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-blue-100 rounded-lg">
            <LockIcon className="w-5 h-5 text-blue-600" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-neutral-900">Export & backup is a paid feature</h3>
            <p className="text-sm text-neutral-600 mt-1">
              Unlock encrypted backups and therapist-ready exports with a Basic plan.
            </p>
            <div className="mt-4 space-y-2">
              <Button
                onClick={() => {
                  window.location.href = '/pricing';
                }}
                className="w-full gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              >
                <Crown className="w-4 h-4" />
                Upgrade from {plan.toUpperCase()}
              </Button>
            </div>
          </div>
        </div>
      </Card>
    );
  }

  const handleExportData = async () => {
    try {
      const storage = getSecureStorage();
      
      // Export encrypted backup (includes all secure storage data)
      const encryptedBackup = await storage.exportEncryptedBackup();

      // Add metadata wrapper
      const exportData = {
        version: '2.0', // Updated version for encrypted backups
        exportDate: new Date().toISOString(),
        encrypted: true,
        data: encryptedBackup,
      };

      // Create JSON blob
      const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tension-encrypted-backup-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);

      toast.success('🔒 Encrypted backup exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error('Export failed. Please try again.');
    }
  };

  const handleImportData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const importData = JSON.parse(e.target?.result as string);

        // Validate data structure
        if (!importData.version || !importData.exportDate) {
          toast.error('Invalid backup file');
          return;
        }

        // Confirm before overwriting
        const confirmed = window.confirm(
          'This will replace all your current data. Are you sure you want to continue?'
        );

        if (!confirmed) return;

        const storage = getSecureStorage();

        // Check if this is an encrypted backup (v2.0+)
        if (importData.encrypted && importData.data) {
          // Import encrypted backup
          await storage.importEncryptedBackup(importData.data);
          toast.success('🔒 Encrypted backup restored successfully. Reloading...');
        } else {
          // Legacy unencrypted backup (v1.0) - migrate to encrypted storage
          toast.info('Converting legacy backup to encrypted format...');
          
          // Convert old format to encrypted storage
          const legacyKeys = [
            'relationships',
            'activeRelationship',
            'checkins',
            'journalEntries',
            'anxietyTracker',
            'attachmentAssessments',
            'ruminationInterventions',
            'situationalPacks',
            'subscription',
            'onboardingComplete',
          ];

          for (const key of legacyKeys) {
            if (importData[key]) {
              const value = typeof importData[key] === 'string' 
                ? JSON.parse(importData[key]) 
                : importData[key];
              await storage.setItem(`tension_${key}`, value);
            }
          }

          // Import settings
          if (importData.settings) {
            const notificationSettings = {
              enabled: Boolean(importData.settings.notifications),
              dailyReminder: true,
              reminderTime: importData.settings.dailyReminder || '19:00',
            };
            await storage.setItem('tension_notification_settings', notificationSettings);
            // Keep legacy keys mirrored for backward compatibility with older surfaces.
            await storage.setItem('tension_notifications_enabled', importData.settings.notifications);
            await storage.setItem('tension_daily_reminder', importData.settings.dailyReminder);
          }

          toast.success('Legacy backup converted and restored successfully. Reloading...');
        }

        // Reload app after successful import
        setTimeout(() => window.location.reload(), 1500);
      } catch (error) {
        console.error('Import failed:', error);
        toast.error('Invalid backup file format');
      }
    };
    reader.readAsText(file);

    // Reset input
    event.target.value = '';
  };

  const handleDeleteAllData = () => {
    if (deleteConfirmText !== 'DELETE') {
      toast.error('Type DELETE to confirm');
      return;
    }

    // Clear all encrypted secure storage
    const storage = getSecureStorage();
    storage.clear();

    // Also clear migration flag and device ID (for complete wipe)
    localStorage.removeItem('tension_security_migrated');
    localStorage.removeItem('tension_security_migration_date');
    
    // Keep device_id for potential future use
    // localStorage.removeItem('device_id'); // Uncomment for complete wipe

    toast.success('All data permanently deleted. Redirecting...');
    setTimeout(() => {
      window.location.href = '/onboarding';
    }, 1500);
  };

  return (
    <div className="space-y-4">
      {/* Data Stats */}
      <Card className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Database className="w-5 h-5 text-blue-600" />
            <h3 className="font-medium text-neutral-900">Your Data</h3>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="bg-white/60 rounded-lg p-3 text-center">
              <p className="text-2xl font-light text-neutral-900">{stats.checkins}</p>
              <p className="text-xs text-neutral-600">Check-Ins</p>
            </div>
            <div className="bg-white/60 rounded-lg p-3 text-center">
              <p className="text-2xl font-light text-neutral-900">{stats.journal}</p>
              <p className="text-xs text-neutral-600">Journal Entries</p>
            </div>
            <div className="bg-white/60 rounded-lg p-3 text-center">
              <p className="text-2xl font-light text-neutral-900">{stats.anxiety}</p>
              <p className="text-xs text-neutral-600">Anxiety Logs</p>
            </div>
            <div className="bg-white/60 rounded-lg p-3 text-center">
              <p className="text-2xl font-light text-neutral-900">{stats.relationships}</p>
              <p className="text-xs text-neutral-600">Relationships</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Export Data */}
      <Card className="p-6">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Download className="w-5 h-5 text-neutral-600" />
            <h3 className="font-medium text-neutral-900">Export Backup</h3>
          </div>

          <div className="tension-glow bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
            <div className="flex items-start gap-2">
              <Shield className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-green-800">
                <strong>Encrypted Backup:</strong> Your data is exported in encrypted format.
                Only you can decrypt it with your device fingerprint.
              </p>
            </div>
          </div>

          <p className="text-sm text-neutral-600">
            Download all your TENSION data as an encrypted JSON file. Store it safely as a backup.
          </p>

          <Button onClick={handleExportData} variant="outline" className="w-full gap-2">
            <Shield className="w-4 h-4" />
            Download Encrypted Backup
          </Button>
        </div>
      </Card>

      {/* Import Data */}
      <Card className="p-6">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Upload className="w-5 h-5 text-neutral-600" />
            <h3 className="font-medium text-neutral-900">Restore from Backup</h3>
          </div>

          <p className="text-sm text-neutral-600">
            Upload a previous backup file to restore your data. This will replace all current data.
          </p>

          <div className="tension-glow bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-yellow-800">
                Warning: This will overwrite all existing data. Export a backup first if needed.
              </p>
            </div>
          </div>

          <label className="block">
            <input
              type="file"
              accept=".json"
              onChange={handleImportData}
              className="hidden"
              id="import-file"
            />
            <Button variant="outline" className="w-full gap-2" asChild>
              <span className="cursor-pointer">
                <Upload className="w-4 h-4" />
                Upload Backup File
              </span>
            </Button>
          </label>
        </div>
      </Card>

      {/* Delete All Data */}
      <Card className="p-6 border-red-200">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-red-600" />
            <h3 className="font-medium text-red-900">Delete All Data</h3>
          </div>

          <p className="text-sm text-neutral-600">
            Permanently delete all TENSION data from this device. This cannot be undone.
          </p>

          {!showDeleteConfirm ? (
            <Button
              onClick={() => setShowDeleteConfirm(true)}
              variant="outline"
              className="w-full border-red-300 text-red-700 hover:bg-red-50"
            >
              Delete All Data
            </Button>
          ) : (
            <div className="space-y-3">
              <div className="tension-glow bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-900 font-medium mb-2">
                  ⚠️ This will permanently delete:
                </p>
                <ul className="text-xs text-red-800 space-y-1">
                  <li>• All {stats.checkins} check-ins</li>
                  <li>• All {stats.journal} journal entries</li>
                  <li>• All {stats.anxiety} anxiety logs</li>
                  <li>• All {stats.relationships} relationship(s)</li>
                  <li>• All insights and assessments</li>
                </ul>
              </div>

              <div>
                <label className="text-sm text-neutral-900 block mb-2">
                  Type <strong>DELETE</strong> to confirm:
                </label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="DELETE"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    setShowDeleteConfirm(false);
                    setDeleteConfirmText('');
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleDeleteAllData}
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  disabled={deleteConfirmText !== 'DELETE'}
                >
                  Delete Forever
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Privacy Note */}
      <div className="tension-glow bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-green-900 mb-1">
              Privacy-First Design
            </p>
            <p className="text-xs text-green-700">
              All data is stored locally on your device. TENSION servers never see your relationship data.
              Your backups are yours to keep, share with therapists, or delete at any time.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
